import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';

import SaveIcon from '@mui/icons-material/Save';
import { Box, Theme, Typography } from '@mui/material';

import { ClientCreateFormSubmitProps } from './types';

import {
  checkClientUserIdAvailability,
  createClientRecord,
  getClientAutoGenerateConfig,
} from '../../api/clientsApi/clientsApi';
import Loader from '../../components/Loader';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormDateField from '../../components/formFields/FormDateField';
import FormTextField from '../../components/formFields/FormTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import { US_STATES_LIST } from '../../constants/constants';
import { CLIENT_CREATE_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { clientCreateFormSchema } from '../../helpers/formSchemas/formSchemas';
import useSnackBar from '../../hooks/useSnackBar';
import FormMaskedTextField from '../../components/formFields/FormMaskedTextField';
import OnboardingConfigurationCard from './OnboardingConfigurationCard';
import {
  normalizeOnboardingOptionValue,
} from './onboardingConfiguration';

const useStyles = makeStyles()((theme: Theme) => ({
  formContainer: {
    padding: '32px',
    flexBasis: '896px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 1000px)': {
      flexBasis: '480px',
      padding: '12px',
    },
  },
  formWrapper: {
    display: 'flex',
  },
  formInfoContainer: {
    display: 'flex',
    gap: '64px',

    '@media (max-width: 1000px)': {
      flexDirection: 'column',
      gap: 0,
    },
  },
  formInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 1000px)': {
      flexBasis: 0,
    },
  },
  formSecondInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 1000px)': {
      flexBasis: 0,
      marginTop: '32px',
    },
  },
  formPlaceInfoContainer: {
    marginTop: '24px',
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',

    '@media (max-width: 1000px)': {
      gap: '24px',
    },
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
}));

const ClientCreateFormContainer = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();

  const firmId = localStorage.getItem('firmId');

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isUserIdTaken, setIsUserIdTaken] = useState(false);
  const [userIdHelperText, setUserIdHelperText] = useState('');
  const [userIdHelperType, setUserIdHelperType] = useState<'success' | 'warning' | 'error' | null>(
    null,
  );
  const [autoGenerateConfig, setAutoGenerateConfig] = useState<{
    autoGenerateClientEmail: boolean;
    autoGenerateClientAddress: boolean;
  } | null>(null);

  const formMethods = useForm<ClientCreateFormSubmitProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(clientCreateFormSchema) as never,
    defaultValues: CLIENT_CREATE_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { isValid, isDirty },
  } = formMethods;

  const autoGenerateClientEmail = watch('autoGenerateClientEmail');
  const autoGenerateClientAddress = watch('autoGenerateClientAddress');
  const watchedUserId = watch('userId');
  const userIdFieldError = formMethods.formState.errors.userId?.message as string | undefined;

  useEffect(() => {
    // Any edit invalidates the previous availability result
    setIsUserIdTaken(false);
    // Drop any stale availability message while the user is typing
    setUserIdHelperText('');
    setUserIdHelperType(null);
    if (!watchedUserId?.trim()) {
      setUserIdHelperText('Leave blank to auto-generate. If a customer with the same SSN and DOB already exists, their existing User ID is used instead.');
      setUserIdHelperType(null);
    }
  }, [watchedUserId]);

  useEffect(() => {
    getClientAutoGenerateConfig()
      .then((res) => {
        setAutoGenerateConfig(res.data);
        setValue('autoGenerateClientEmail', res.data.autoGenerateClientEmail);
        setValue('autoGenerateClientAddress', res.data.autoGenerateClientAddress);
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  }, []);

  const onSubmitForm = (values: ClientCreateFormSubmitProps) => {
    if (isUserIdTaken) {
      setAlert((prev) => ({
        ...prev,
        message: 'User ID is already taken. Choose a different one.',
        type: 'error',
      }));
      return;
    }
    setIsFormSubmitting(true);

    const selectedOnboardingOption = normalizeOnboardingOptionValue(values.onboardingOption);

    const submitData = {
      ...(values.middleName?.length && { middleName: values.middleName }),
      ...(values.address2?.length && { address2: values.address2 }),
      ...(values.ssn?.trim()?.length && { ssn: values.ssn }),
      ...(values.birthdate &&
        moment(values.birthdate).isValid() && {
          birthdate: moment(values.birthdate).format('YYYY-MM-DD'),
        }),
      referenceId: values.referenceId,
      firstName: values.firstName,
      lastName: values.lastName,
      ...(values.userId?.trim()?.length && { userId: values.userId.trim().toLowerCase() }),
      ...(values.email?.trim()?.length && { email: values.email }),
      ...(values.phone?.trim()?.length && { phone: values.phone }),
      ...(values.address1?.trim()?.length && { address1: values.address1 }),
      ...(values.city?.trim()?.length && { city: values.city }),
      ...(values.zip?.trim()?.length && { zip: values.zip }),
      firmId,
      ...(values.state?.length && { countryState: values.state }),
      onboardingConfiguration: {
        authType: selectedOnboardingOption,
        linkDeliveryChannel: values.onboardingLinkDeliveryChannel || null,
        otpChannel: values.onboardingOtpChannel || null,
      },
    };

    createClientRecord(submitData)
      .then((res) => {
        if (!res.data.error) {
          setIsFormSubmitting(false);
          navigate(`/clients/${res.data.id}/home`);

          setAlert((prev) => ({
            ...prev,
            message: res.data.userIdReused
              ? `Client created successfully. This person already has an account, so the existing User ID "${res.data.userId}" was used${
                  submitData.userId ? ' and the User ID you entered was ignored' : ''
                }.`
              : 'Client created successfully.',
            type: res.data.userIdReused ? 'warning' : 'success',
          }));
        }
      })
      .catch((err) => {
        setIsFormSubmitting(false);
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleCancelChanges = () => reset();

  const handleUserIdBlur = async () => {
    const isUserIdValid = await trigger('userId');
    if (!isUserIdValid) {
      setUserIdHelperText('');
      setUserIdHelperType(null);
      return;
    }

    const userId = getValues('userId')?.trim().toLowerCase();
    // Reflect the normalized (trimmed, lowercased) value back in the input
    setValue('userId', userId ?? '', { shouldDirty: true });
    if (!userId) {
      setUserIdHelperText('Leave blank to auto-generate. If a customer with the same SSN and DOB already exists, their existing User ID is used instead.');
      setUserIdHelperType(null);
      return;
    }

    checkClientUserIdAvailability({ userId })
      .then((res) => {
        if (res.data.available) {
          setIsUserIdTaken(false);
          setUserIdHelperText('User ID is available.');
          setUserIdHelperType('success');
        } else {
          setIsUserIdTaken(true);
          setUserIdHelperText('User ID is already taken. Choose a different one.');
          setUserIdHelperType('error');
        }
      })
      .catch((err) => {
        setUserIdHelperType('error');
        setUserIdHelperText(err?.response?.data?.message || 'Unable to validate User ID right now.');
      });
  };

  return (
    <>
      {!firmId || !autoGenerateConfig ? (
        <Loader colorType="warning" />
      ) : (
        <>
          <Box className={styles.classes.formWrapper}>
            <FormProvider {...formMethods}>
              <form className={styles.classes.formContainer}>
                <Box className={styles.classes.formInfoContainer}>
                  <Box className={styles.classes.formInfoBlock}>
                    <Typography className={styles.classes.fieldHeadline}>Identity</Typography>
                    <Box mt="16px">
                      <FormTextField name="referenceId" label="Reference Id *" />
                    </Box>
                    <Box mt="24px">
                      <FormTextField name="firstName" label="First Name *" />
                    </Box>
                    <Box mt="24px">
                      <FormTextField name="middleName" label="Middle Name" />
                    </Box>
                    <Box mt="24px">
                      <FormTextField name="lastName" label="Last Name *" />
                    </Box>
                    <Box mt="24px">
                      <FormTextField
                        name="userId"
                        label="User ID"
                        onBlur={() => {
                          void handleUserIdBlur();
                        }}
                        inputProps={{ maxLength: 80 }}
                      />
                      <Typography
                        mt="6px"
                        sx={{
                          fontFamily: 'Inter',
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: userIdFieldError
                            ? '#F06A6A'
                            : userIdHelperType === 'success'
                              ? '#5ECF95'
                              : userIdHelperType === 'warning'
                                ? styles.theme.palette.warning.main
                                : userIdHelperType === 'error'
                                  ? '#F06A6A'
                                  : 'rgba(255,255,255,0.7)',
                        }}
                      >
                        {userIdFieldError ? ' ' : userIdHelperText || 'Leave blank to auto-generate. If a customer with the same SSN and DOB already exists, their existing User ID is used instead.'}
                      </Typography>
                    </Box>

                    <Typography className={styles.classes.fieldHeadline} mt="32px">
                      Credentials
                    </Typography>
                    <Box mt="16px">
                      <FormTextField name="email" label={autoGenerateClientEmail ? 'Email Address' : 'Email Address *'} />
                    </Box>
                    <Box mt="24px">
                      <FormMaskedTextField
                        mask="+19999999999"
                        name="phone"
                        label="Mobile Number"
                      />
                    </Box>
                  </Box>

                  <Box className={styles.classes.formSecondInfoBlock}>
                    <Typography className={styles.classes.fieldHeadline}>Address</Typography>
                    <Box mt="16px">
                      <FormTextField name="address1" label={autoGenerateClientAddress ? 'Address Line 1' : 'Address Line 1 *'} />
                    </Box>
                    <Box mt="24px">
                      <FormTextField name="address2" label="Address Line 2" />
                    </Box>
                    <Box className={styles.classes.formPlaceInfoContainer}>
                      <FormTextField name="city" label={autoGenerateClientAddress ? 'City' : 'City *'} />
                      <FormSelectField
                        options={US_STATES_LIST}
                        name="state"
                        label={autoGenerateClientAddress ? 'State' : 'State *'}
                        width="100%"
                      />
                      <FormTextField name="zip" label={autoGenerateClientAddress ? 'Zip' : 'Zip *'} />
                    </Box>

                    <Typography className={styles.classes.fieldHeadline} mt="32px">
                      Personal Information
                    </Typography>
                    <Box mt="16px">
                      <FormDateField name="birthdate" label="Date of Birth" maxDate={moment()} />
                    </Box>
                    <Box mt="24px">
                      <FormMaskedTextField
                        mask="999-99-9999"
                        name="ssn"
                        label="Social Security Number"
                      />
                    </Box>

                  </Box>
                </Box>

                {/* Leave room for the absolutely-positioned helper text of the last field above. */}
                <Box mt="32px">
                  <OnboardingConfigurationCard widthMode="half" />
                </Box>

                <Box display="flex" width="100%" justifyContent="center">
                  <Box
                    display="flex"
                    mt="32px"
                    maxWidth="416px"
                    flexDirection="column"
                    gap="16px"
                    width="100%"
                  >
                    <FormSubmitButton
                      disabled={!isValid || isFormSubmitting}
                      buttonIcon={<SaveIcon />}
                      buttonText="Save"
                      onClick={handleSubmit(onSubmitForm)}
                    />
                    <FormCancelButton disabled={!isDirty} onClick={handleCancelChanges} />
                  </Box>
                </Box>
              </form>
            </FormProvider>
          </Box>
        </>
      )}
    </>
  );
};

export default ClientCreateFormContainer;
